/**
 * ProviderCoverage.tsx
 * ---------------------------------------------------------------------------
 * Replaces the accordion soup at the bottom of the old result popup.
 *
 * A matrix of every provider with a status dot — returned data / answered
 * empty / failed — so "Hiya and Eyecon failed" is visible at a glance instead
 * of being buried. Only providers that returned data expand.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { cleanScalar, ProviderSummary } from '../adapters/providers'
import { BreachList, FieldTable, ResCard, Sech, ShowMoreButton, SourceBadge, Stats, useShowMore, useTokens } from './primitives'
import { radii } from '../tokens'

const DOT: Record<string, string> = { data: '#10B981', empty: '#94A3B8', failed: '#EF4444' }

/**
 * Same app-name -> logo lookup GlookCommon.tsx (the legacy popup's own Glook
 * renderer) already uses for this exact "Applications" list — ported here
 * rather than shared, since that component isn't part of this redesign.
 * Unmapped app names (most third-party ones — Chess.com, Adobe, ...) fall
 * back to a plain "?" glyph instead of a broken-image icon.
 */
const DEFAULT_APP_ICON =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23666'><path d='M12 2a10 10 0 100 20 10 10 0 000-20zm0 17a1.25 1.25 0 110-2.5A1.25 1.25 0 0112 19zm1.2-5.8c-.8.5-1 1-1 1.8h-2c0-1.5.5-2.4 1.8-3.2.9-.6 1.2-1 1.2-1.8 0-1-0.8-1.8-2-1.8-1.1 0-2 .7-2 1.8H7c0-2.2 1.9-3.8 4.2-3.8 2.4 0 4.2 1.6 4.2 3.8 0 1.5-.7 2.4-2.2 3.2z'/></svg>"

const APP_ICONS: Record<string, string> = {
  search: 'https://cdn.simpleicons.org/google',
  chrome: 'https://cdn.simpleicons.org/googlechrome',
  gmail: 'https://cdn.simpleicons.org/gmail',
  drive: 'https://cdn.simpleicons.org/googledrive',
  docs: 'https://cdn.simpleicons.org/googledocs',
  sheets: 'https://cdn.simpleicons.org/googlesheets',
  slides: 'https://cdn.simpleicons.org/googleslides',
  forms: 'https://cdn.simpleicons.org/googleforms',
  meet: 'https://cdn.simpleicons.org/googlemeet',
  googlemeet: 'https://cdn.simpleicons.org/googlemeet',
  chat: 'https://cdn.simpleicons.org/googlechat',
  calendar: 'https://cdn.simpleicons.org/googlecalendar',
  googlecalendar: 'https://cdn.simpleicons.org/googlecalendar',
  keep: 'https://cdn.simpleicons.org/googlekeep',
  youtube: 'https://cdn.simpleicons.org/youtube',
  youtubemusic: 'https://cdn.simpleicons.org/youtubemusic',
  photos: 'https://cdn.simpleicons.org/googlephotos',
  googlephotos: 'https://cdn.simpleicons.org/googlephotos',
  podcasts: 'https://cdn.simpleicons.org/googlepodcasts',
  maps: 'https://cdn.simpleicons.org/googlemaps',
  googlemaps: 'https://cdn.simpleicons.org/googlemaps',
  earth: 'https://cdn.simpleicons.org/googleearth',
  googlecloud: 'https://cdn.simpleicons.org/googlecloud',
  firebase: 'https://cdn.simpleicons.org/firebase',
  analytics: 'https://cdn.simpleicons.org/googleanalytics',
  ads: 'https://cdn.simpleicons.org/googleads',
  adsense: 'https://cdn.simpleicons.org/googleadsense',
  tagmanager: 'https://cdn.simpleicons.org/googletagmanager',
  playconsole: 'https://cdn.simpleicons.org/googleplay',
  android: 'https://cdn.simpleicons.org/android',
  wearos: 'https://cdn.simpleicons.org/wearos',
  chromecast: 'https://cdn.simpleicons.org/googlecast',
  classroom: 'https://cdn.simpleicons.org/googleclassroom',
  translate: 'https://cdn.simpleicons.org/googletranslate',
  news: 'https://cdn.simpleicons.org/googlenews',
  authenticator: 'https://cdn.simpleicons.org/googleauthenticator',
  googleplay: 'https://cdn.simpleicons.org/googleplay',
  spotify: 'https://cdn.simpleicons.org/spotify',
  duolingo: 'https://cdn.simpleicons.org/duolingo',
  twitter: 'https://cdn.simpleicons.org/x',
  x: 'https://cdn.simpleicons.org/x',
}

// `.replace(" ", "")` (single, not global) in the legacy version meant
// "Google Maps" never actually matched "googlemaps" — stripped every space,
// not just the first, so multi-word app names resolve too.
const appIconOf = (name: string): string => APP_ICONS[name.trim().toLowerCase().replace(/\s+/g, '')] || DEFAULT_APP_ICON

function AppChip({ name }: { name: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 11.5, fontWeight: 600,
        px: '10px', py: '4px', borderRadius: '8px', background: 'rgba(45,137,244,.12)', color: '#1559B0',
      }}
    >
      <Box component="img" src={appIconOf(name)} alt="" sx={{ width: 14, height: 14, borderRadius: '3px', flex: 'none', objectFit: 'contain' }} />
      {name}
    </Box>
  )
}

function AppChips({ items }: { items: string[] }) {
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 10)
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {visible.map((name, i) => <AppChip key={i} name={name} />)}
      </Box>
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

/**
 * Glook's own Google-account snapshot — Local Guide points/level/reviews
 * (from `gaia.details.contributor` or `gaia.contributor`, depending on which
 * Glook sub-probe answered), a Wayback follower count, and the account's
 * connected apps (Gmail's own reachability list plus whatever `email_blkb`
 * found). Rendered as a friendly summary above the raw field table, only for
 * the `glook` group and only when there's actually something to show — a
 * `{ found: false, message: "No profile snapshot found" }` response already
 * reads fine as plain rows and has nothing this would add.
 */
function glookActivityOf(raw: any): { stats: { label: string; value: string }[]; applications: string[] } | null {
  if (!raw || typeof raw !== 'object') return null
  const contributor = raw?.gaia?.details?.contributor ?? raw?.gaia?.contributor
  const c = contributor?.contributions ?? {}
  const followers = raw?.wayback?.profile?.followers
  const statDefs: [string, any][] = [
    ['Followers', followers],
    ['Points', contributor?.points],
    ['Level', contributor?.level],
    ['Local Guide', contributor?.local_guide ? 'Yes' : undefined],
    ['Reviews', c.reviews],
    ['Ratings', c.ratings],
    ['Answers', c.answers],
    ['Photos', c.photos],
    ['Videos', c.videos],
    ['Edits', c.edits],
    ['Q&A', c.q_a],
  ]
  const stats = statDefs
    .filter(([, v]) => v !== undefined && v !== null && v !== 0 && v !== '')
    .map(([label, value]) => ({ label, value: typeof value === 'number' ? value.toLocaleString() : String(value) }))

  const gmailApps: string[] = Array.isArray(raw?.email_info?.PROFILE_CONTAINER?.profile?.inAppReachability?.PROFILE?.apps)
    ? raw.email_info.PROFILE_CONTAINER.profile.inAppReachability.PROFILE.apps
    : []
  const blkbApps: string[] = Array.isArray(raw?.email_blkb?.results)
    ? raw.email_blkb.results.map((r: any) => cleanScalar(r?.name ?? '')).filter(Boolean)
    : []
  const applications = Array.from(new Set([...gmailApps, ...blkbApps].map((a) => String(a).trim()).filter(Boolean)))

  if (stats.length === 0 && applications.length === 0) return null
  return { stats, applications }
}

/**
 * `hbp` (HaveIBeenPwned, via Glook) — same breach array the Breaches card
 * renders with `BreachList`, reused here rather than left as the generic
 * `[["items", "7"]]` field-count row every other array-shaped provider gets.
 * `emailSearchResultHbp` can carry a leading `{ criteria: "..." }` stub (an
 * echo of the search input, not a breach) alongside the real entries — a
 * real breach always has a name, the stub never does.
 */
function hbpBreachesOf(raw: any): any[] | null {
  if (!Array.isArray(raw)) return null
  const breaches = raw.filter((b: any) => Boolean(b?.Title || b?.Name))
  return breaches.length > 0 ? breaches : null
}

/**
 * `smf` ("Social Media Finder") — same hit-filtering PersonTemplate's own
 * "Social profiles" card applies (most of the ~20 probed networks come back
 * with `socialProfileUrl: null`, a miss already accounted for in this
 * panel's own empty/failed tally), reused here so expanding "smf" shows the
 * same readable social+link list instead of the generic `[["items","20"]]`
 * field-count row every other array-shaped provider falls back to.
 */
function smfLinksOf(raw: any): { social: string; url: string }[] | null {
  if (!Array.isArray(raw)) return null
  const links = raw
    .filter((r: any) => r && typeof r === 'object' && r.social && r.socialProfileUrl)
    .map((r: any) => ({ social: String(r.social), url: String(r.socialProfileUrl) }))
  return links.length > 0 ? links : null
}

/** Own component (not inlined in the openKeys.map below) so its `useShowMore`
 * call has a stable, unconditional hook count regardless of how many
 * provider panels happen to be open — same reason `BreachList` is its own
 * component rather than a hook call inline in the map. */
function SmfLinksPreview({ links }: { links: { social: string; url: string }[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(links, 5)
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {visible.map((s) => (
          <Box
            key={s.social}
            component="a"
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none',
              borderRadius: '9px', p: '5px 6px', ml: '-6px',
              '&:hover': { background: tk.surface3 },
            }}
          >
            <SourceBadge source={s.social} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tk.text, textTransform: 'capitalize', flex: 'none' }}>
              {s.social}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5, color: tk.text3, ml: 'auto', minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {s.url.replace(/^https?:\/\//, '')}
            </Typography>
          </Box>
        ))}
      </Box>
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

export function ProviderCoverage({ summary, tone }: { summary: ProviderSummary; tone?: 'accent' }) {
  const tk = useTokens()
  const [openKeys, setOpenKeys] = React.useState<Set<string>>(
    () => new Set(summary.withData[0] ? [summary.withData[0].key] : []),
  )

  if (!summary.withData.length) return null

  const toggle = (key: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // Button reflects "is anything open at all" rather than "is everything
  // open" — otherwise, with the default single panel already expanded, the
  // first click would expand the rest instead of collapsing that default one
  // like the arrow (pointing up, i.e. "collapse") promises.
  const anyOpen = openKeys.size > 0
  const toggleAll = () => setOpenKeys(anyOpen ? new Set() : new Set(summary.withData.map((g) => g.key)))

  // Only providers that actually returned data — "answered, empty" and
  // "failed" rows added noise without adding information (a provider that
  // came back empty isn't a coverage gap worth a whole tile), so both the
  // grid and its count badge only reflect summary.withData now.
  return (
    <ResCard title="Provider coverage" count={summary.withData.length} tone={tone}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(126px, 1fr))', gap: '6px' }}>
        {summary.groups.filter((g) => g.status === 'data').map((g) => (
          <Box
            key={g.key}
            component="button"
            onClick={() => g.status === 'data' && toggle(g.key)}
            sx={{
              display: 'flex', alignItems: 'center', gap: '7px', p: '8px 9px', width: '100%', textAlign: 'left',
              borderRadius: '9px', border: `1px solid ${openKeys.has(g.key) ? '#2D89F4' : tk.border}`,
              background: tk.surface2, fontSize: 11.5, fontFamily: 'inherit',
              cursor: g.status === 'data' ? 'pointer' : 'default',
              fontWeight: g.status === 'data' ? 600 : 500,
              color: g.status === 'data' ? tk.text : tk.text3,
              '&:hover': { borderColor: g.status === 'data' ? '#2D89F4' : tk.border },
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', background: DOT[g.status], boxShadow: `0 0 0 3px ${DOT[g.status]}22` }} />
            <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.key}</Box>
            {g.merged > 1 && <Box component="span" sx={{ ml: 'auto', fontSize: 9.5, color: tk.text3 }}>×{g.merged}</Box>}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: 10.5, color: tk.text3, mt: '10px', flexWrap: 'wrap' }}>
        {summary.duplicatesDropped > 0 && <span>{summary.duplicatesDropped} duplicate responses merged</span>}
        {summary.withData.length > 0 && (
          <Box
            component="button"
            type="button"
            onClick={toggleAll}
            title={anyOpen ? 'Collapse all' : 'Expand all'}
            aria-label={anyOpen ? 'Collapse all' : 'Expand all'}
            sx={{
              display: 'grid', placeItems: 'center', width: 26, height: 26, flex: 'none', ml: 'auto',
              borderRadius: '50%', border: `1px solid ${tk.border}`, background: tk.surface2,
              color: tk.text2, cursor: 'pointer',
              '&:hover': { background: tk.surface3 },
            }}
          >
            <svg
              width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
              strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: anyOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </Box>
        )}
      </Box>

      {/*
        Rendered in reverse insertion-order of `openKeys` (a Set, so
        iteration order already follows add order, and re-toggling a key
        moves it back to the end) rather than `summary.withData`'s fixed
        richness order — the panel you just clicked open should land above
        whatever was already open, not wherever its data-richness rank
        happens to place it.
      */}
      {Array.from(openKeys).reverse().map((key) => {
        const g = summary.withData.find((row) => row.key === key)
        if (!g) return null
        const activity = g.key === 'glook' ? glookActivityOf(g.raw) : null
        const hbpBreaches = g.key === 'hbp' ? hbpBreachesOf(g.raw) : null
        const smfLinks = g.key === 'smf' ? smfLinksOf(g.raw) : null
        return (
          <Box key={g.key} sx={{ mt: '12px', border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, background: tk.surface2, p: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '10px' }}>
              <SourceBadge source={g.key} />
              <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: tk.text }}>{g.key}</Typography>
              <Typography sx={{ ml: 'auto', fontSize: 10.5, color: tk.text3 }}>
                {g.fields.length} fields{g.merged > 1 ? ` · ${g.merged} merged` : ''}
              </Typography>
            </Box>
            {activity && (
              <Box sx={{ mb: '12px' }}>
                {activity.stats.length > 0 && <Stats items={activity.stats} />}
                {activity.applications.length > 0 && (
                  <Box sx={{ mt: activity.stats.length > 0 ? '10px' : 0 }}>
                    <Sech count={activity.applications.length}>Applications</Sech>
                    <AppChips items={activity.applications} />
                  </Box>
                )}
              </Box>
            )}
            {hbpBreaches && (
              <Box sx={{ mb: '12px' }}>
                <BreachList breaches={hbpBreaches} cap={5} />
              </Box>
            )}
            {smfLinks && (
              <Box sx={{ mb: '12px' }}>
                <SmfLinksPreview links={smfLinks} />
              </Box>
            )}
            <FieldTable rows={g.fields.slice(0, 24)} />
            {g.note && (
              <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '8px', pt: '8px', borderTop: `1px dashed ${tk.border}` }}>{g.note}</Typography>
            )}
          </Box>
        )
      })}
    </ResCard>
  )
}
